// Maps a raw item from either UGC platform onto the common shape VideoCard /
// VideoDetailSection render, so those components branch on a handful of
// explicit fields (`platform`, `isMetaBased`, `is_reel`) instead of every
// caller having to know which platform's raw field names to reach for.
//
// YouTube's response items ("videos" in RECENT_VIDEO_FILTERS.md) already
// match the shape this page was built around, so they pass through mostly
// unchanged. Instagram's response items ("reels" in INSTAGRAM_REEL_API.md §5)
// use different field names for the same concepts - this is where that
// translation happens, once, instead of scattered through the UI.
import { formatDuration, formatRelativeTime } from "./utils"

const igDisplayName = (reel) =>
  (reel.full_name && reel.full_name.trim()) || (reel.username ? `@${reel.username}` : "Unknown creator")

const igTitle = (reel) => {
  const caption = (reel.caption || "").trim()
  if (caption) return caption
  return `Instagram Reel by ${reel.username ? `@${reel.username}` : "an unknown creator"}`
}

const ttDisplayName = (video) =>
  (video.full_name && video.full_name.trim()) || (video.username ? `@${video.username}` : "Unknown creator")

const ttTitle = (video) => {
  const caption = (video.caption || "").trim()
  if (caption) return caption
  return `TikTok video by ${video.username ? `@${video.username}` : "an unknown creator"}`
}

// Instagram calls the same per-page-computed signal object's creator count
// `reels_in_store` where YouTube calls it `videos_in_store` (both doc'd under
// `signals.creator` - RECENT_VIDEO_FILTERS.md §6 / INSTAGRAM_REEL_API.md §5).
// Normalizing the key here means VideoCard's tooltip logic never has to know
// which platform it's looking at.
const normalizeCreatorSignal = (creator) => {
  if (!creator) return creator
  if (creator.videos_in_store !== undefined) return creator
  return { ...creator, videos_in_store: creator.reels_in_store }
}

const normalizeSignals = (signals) =>
  signals ? { ...signals, creator: normalizeCreatorSignal(signals.creator) } : signals

export const normalizeUgcItem = (raw, platform) => {
  if (!raw) return raw

  if (platform === "tiktok") {
    return {
      ...raw,
      platform: "tiktok",
      title: ttTitle(raw),
      channel_name: ttDisplayName(raw),
      channel_url: raw.profile_url,
      // `thumb_url` is this app's own S3-hosted mirror of TikTok's `cover_url`,
      // made the moment the video is stored specifically because every image
      // URL TikTok hands out is *signed* and expires within days while this
      // store keeps videos for years - prefer it, falling back to the raw
      // (eventually-dead) `cover_url` only if a mirror hasn't been made yet
      // (TIKTOK_VIDEO_API.md §1/§5). There's no `profile_thumb_url` equivalent -
      // TikTok's search payload reports no creator avatar at all, so
      // `profile_pic_url` is deliberately left unset and Avatar just self-hides.
      thumbnail_url: raw.thumb_url || raw.cover_url,
      duration_text: formatDuration(raw.duration_seconds),
      published_time_text: formatRelativeTime(raw.published_at),
      // `url` is the video's real tiktok.com permalink (§5) - the only "watch"
      // link this payload offers; unlike Instagram there's no raw playable
      // file field to try inline, so the UI always links out rather than
      // embedding a <video>.
      url: raw.url,
      is_tiktok: true,
      // `analysis_basis: "metadata"` is TikTok's "no caption written, judged
      // from tags/account/sound instead" signal, same spelling as Instagram's
      // (§5).
      isMetaBased: raw.analysis_basis === "metadata",
      signals: normalizeSignals(raw.signals),
    }
  }

  if (platform !== "instagram") {
    return {
      ...raw,
      platform: "youtube",
      // `analysis_basis: "title_description"` is YouTube's "no transcript,
      // summarized from metadata instead" signal (API doc, §5/§6).
      isMetaBased: raw.analysis_basis === "title_description",
      signals: normalizeSignals(raw.signals),
    }
  }

  return {
    ...raw,
    platform: "instagram",
    video_id: raw.reel_id,
    title: igTitle(raw),
    channel_name: igDisplayName(raw),
    channel_url: raw.profile_url,
    // `thumb_url`/`profile_thumb_url` are this app's own S3-hosted mirrors of
    // Instagram's `thumbnail_url`/`profile_pic_url`, made specifically so the
    // UI doesn't have to fight Instagram's CDN hotlink protection - prefer
    // them, falling back to the raw Instagram CDN field only if a mirror
    // hasn't been generated for this reel yet.
    thumbnail_url: raw.thumb_url || raw.thumbnail_url,
    profile_pic_url: raw.profile_thumb_url || raw.profile_pic_url,
    duration_text: formatDuration(raw.duration_seconds),
    published_time_text: formatRelativeTime(raw.published_at),
    // `url` (the reel's real instagram.com/reel/<shortcode>/ permalink,
    // §5) is what every "watch"/"open" affordance in the UI must link to -
    // never `video_url` (the raw playable file) or `thumbnail_url`. Falling
    // back to a shortcode-built permalink is just insurance against a
    // response that omits `url` but still has `shortcode`.
    url: raw.url || (raw.shortcode ? `https://www.instagram.com/reel/${raw.shortcode}/` : raw.url),
    is_reel: true,
    // Account verification (Instagram's blue check) is a different concept
    // from `verification.verified` (this reel verified as being about the
    // app) - kept under its own name so the two never get conflated in the UI.
    account_verified: !!raw.is_verified,
    // `analysis_basis: "metadata"` is Instagram's "no caption written,
    // judged from hashtags/account/audio instead" signal (§5).
    isMetaBased: raw.analysis_basis === "metadata",
    signals: normalizeSignals(raw.signals),
  }
}
